import React, { useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { api, fileUrl } from '@/lib/api';
import { Heart, Trash2, MessageCircle, User as UserIcon, Shield, Maximize2, Radio, Users, LogIn, Square } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '@/lib/errors';
import CommentThread from '@/components/CommentNode';
import ChatComposer from '@/components/ChatComposer';
import UserLink from '@/components/UserLink';
import ImageLightbox from '@/components/ImageLightbox';
import { useI18n } from '@/i18n';
import { Link, useNavigate } from 'react-router-dom';

export default function SpotDetailDrawer({ spot, onClose, onUpdated, onDeleted, onRequireAuth, liveMeetsForSpot = [], onStartLiveMeet, onLiveMeetStopped }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const open = !!spot;
  const [comments, setComments] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!spot) return;
    setComments([]);
    api.get(`/spots/${spot.id}/comments`).then(({ data }) => setComments(data)).catch(() => {});
  }, [spot]);

  // Build tree: top-level comments with their replies attached
  const tree = useMemo(() => {
    const top = comments.filter((c) => !c.parent_id);
    const byParent = {};
    for (const c of comments) {
      if (c.parent_id) {
        if (!byParent[c.parent_id]) byParent[c.parent_id] = [];
        byParent[c.parent_id].push(c);
      }
    }
    return top.map((t) => ({ ...t, replies: byParent[t.id] || [] }));
  }, [comments]);

  if (!spot) return null;

  const isAdmin = user && user !== false && user.role === 'admin';
  const liked = user && user !== false && spot.likes?.includes(user.id);
  const isOwner = user && user !== false && (user.id === spot.author_id || isAdmin);

  const requireAuth = () => {
    if (!user || user === false) { onRequireAuth?.(); return false; }
    return true;
  };

  const canModerateComment = (c) =>
    user && user !== false && (user.id === c.author_id || isAdmin);

  const toggleLike = async () => {
    if (!requireAuth()) return;
    try {
      const { data } = await api.post(`/spots/${spot.id}/like`);
      onUpdated?.({
        ...spot,
        likes: data.liked ? [...(spot.likes || []), user.id] : (spot.likes || []).filter(i => i !== user.id),
        likes_count: data.likes_count,
      });
    } catch { toast.error('Erreur'); }
  };

  const addComment = async ({ text, image_path }) => {
    if (!requireAuth()) return;
    try {
      const { data } = await api.post(`/spots/${spot.id}/comments`, { text, image_path });
      setComments([...comments, data]);
      onUpdated?.({ ...spot, comments_count: (spot.comments_count || 0) + 1 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addReply = async (parentId, payload) => {
    if (!requireAuth()) return;
    // payload may be a string (legacy) or { text, image_path }
    const body = typeof payload === 'string'
      ? { text: payload, parent_id: parentId }
      : { text: payload.text, image_path: payload.image_path, parent_id: parentId };
    try {
      const { data } = await api.post(`/spots/${spot.id}/comments`, body);
      setComments([...comments, data]);
      onUpdated?.({ ...spot, comments_count: (spot.comments_count || 0) + 1 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteComment = async (c) => {
    if (!window.confirm(t('spot.comment.confirm_delete'))) return;
    try {
      const { data } = await api.delete(`/spots/${spot.id}/comments/${c.id}`);
      setComments(comments.filter((x) => x.id !== c.id && x.parent_id !== c.id));
      onUpdated?.({ ...spot, comments_count: Math.max(0, (spot.comments_count || 0) - (data.deleted || 1)) });
      toast.success(t('spot.comment.deleted'));
    } catch { toast.error('Erreur'); }
  };

  const likeComment = async (c) => {
    if (!requireAuth()) return;
    try {
      const { data } = await api.post(`/spots/${spot.id}/comments/${c.id}/like`);
      setComments((prev) => prev.map((x) => {
        if (x.id !== c.id) return x;
        const likes = data.liked
          ? [...(x.likes || []), user.id]
          : (x.likes || []).filter((i) => i !== user.id);
        return { ...x, likes, likes_count: data.likes_count };
      }));
    } catch { toast.error('Erreur'); }
  };

  const remove = async () => {
    if (!window.confirm(t('spot.confirm_delete'))) return;
    try {
      await api.delete(`/spots/${spot.id}`);
      toast.success(t('spot.deleted'));
      onDeleted?.(spot.id);
    } catch { toast.error('Erreur'); }
  };

  const joinConvoy = async (m) => {
    if (!requireAuth()) return;
    try {
      await api.post(`/convoys/${m.convoy_id}/join`);
      toast.success(t('livemeet.joined_convoy') || 'Demande de convoi envoyée');
      navigate(`/convoys/${m.convoy_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t('common.error'));
    }
  };

  const stopLiveMeet = async (m) => {
    if (!window.confirm(t('livemeet.confirm_stop') || 'Arrêter ce car meet live ? Le convoi restera actif.')) return;
    try {
      await api.delete(`/live-meets/${m.id}`);
      toast.success(t('livemeet.stopped') || 'Car meet live arrêté');
      onLiveMeetStopped?.(m.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t('common.error'));
    }
  };

  const CAT_META = {
    car_meet:    { color: '#00F0FF', label: t('map.legend.carmeet') },
    parking:     { color: '#FFD479', label: t('map.cat.parking') },
    gas_station: { color: '#10B981', label: t('map.cat.gas') },
    viewpoint:   { color: '#9DEDFF', label: t('map.cat.viewpoint') },
    spot:        { color: '#A78BFA', label: t('map.cat.spot') },
    s365:        { color: '#FF7A00', label: t('map.cat.s365') || '365' },
  };
  const catMeta = CAT_META[spot.category] || CAT_META.spot;
  const accent = catMeta.color;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="glass-strong border-white/10 text-white w-full sm:max-w-lg overflow-y-auto p-0">
        <div className="relative">
          {spot.image_path ? (
            <div className="bg-black relative overflow-hidden group cursor-pointer" onClick={() => setLightbox(spot.image_path)} data-testid="spot-image-expand">
              <img
                src={fileUrl(spot.image_path)}
                alt={spot.title}
                className="w-full max-h-[60vh] object-contain transition-transform duration-500 group-hover:scale-[1.02] bg-black"
              />
              <div className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm grid place-items-center text-white opacity-0 group-hover:opacity-100 transition">
                <Maximize2 className="w-4 h-4" />
              </div>
            </div>
          ) : (
            <div className="h-32 bg-gradient-to-br from-black to-neutral-900" />
          )}
          <span
            className="absolute top-4 left-4 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] backdrop-blur-md"
            style={{ borderWidth: 1, borderColor: accent, color: accent, background: 'rgba(0,0,0,0.5)' }}
          >
            {catMeta.label}
          </span>
          {isAdmin && (
            <span className="absolute top-4 right-4 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] bg-[#FF007F]/20 border border-[#FF007F] text-[#FF007F] backdrop-blur-md inline-flex items-center gap-1">
              <Shield className="w-3 h-3" /> Admin
            </span>
          )}
        </div>

        <div className="p-6 pt-4">
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className="font-display text-2xl uppercase tracking-tighter text-white">
              {spot.title}
            </SheetTitle>
            <SheetDescription className="text-white/60 flex items-center gap-2 text-xs">
              <UserIcon className="w-3 h-3" /> {t('spot.by')}{' '}
              <UserLink userId={spot.author_id} name={spot.author_name} className="font-semibold text-white/80" />
            </SheetDescription>
          </SheetHeader>

          {spot.description && (
            <p className="text-white/80 text-sm mt-4 leading-relaxed">{spot.description}</p>
          )}

          {/* Actions: like only */}
          <div className="mt-6 flex items-center justify-between">
            <Button
              data-testid="spot-like-btn"
              onClick={toggleLike}
              variant="ghost"
              className={`rounded-full border ${liked ? 'border-[#FF007F] text-[#FF007F] bg-[#FF007F]/10' : 'border-white/10 text-white hover:bg-white/10'}`}
            >
              <Heart className={`w-4 h-4 mr-2 ${liked ? 'fill-current' : ''}`} />
              {spot.likes_count || 0} {(spot.likes_count || 0) > 1 ? t('spot.likes') : t('spot.like_singular')}
            </Button>

            <div className="text-xs text-white/40 inline-flex items-center gap-1">
              <MessageCircle className="w-3 h-3" />
              {spot.comments_count || 0} {(spot.comments_count || 0) > 1 ? t('spot.comments_count_plural') : t('spot.comments_count')}
            </div>
          </div>

          {isOwner && (
            <Button
              data-testid="spot-delete-btn"
              onClick={remove}
              variant="ghost"
              className="mt-3 w-full text-red-300 hover:bg-red-500/10 border border-red-500/20 rounded-full"
            >
              <Trash2 className="w-4 h-4 mr-2" /> {isAdmin && user.id !== spot.author_id ? t('spot.moderate_admin') : t('spot.delete')}
            </Button>
          )}

          {/* Active live car-meets hosted at this spot */}
          {liveMeetsForSpot.length > 0 && (
            <div className="mt-6 rounded-2xl border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-4" data-testid="spot-livemeets-list">
              <div className="flex items-center gap-2 mb-3">
                <Radio className="w-4 h-4 text-[#00F0FF] animate-pulse" />
                <h3 className="font-display font-black uppercase tracking-wider text-sm text-[#00F0FF]">
                  {t('spot.live_here') || 'Car meets live ici'} ({liveMeetsForSpot.length})
                </h3>
              </div>
              <ul className="space-y-2">
                {liveMeetsForSpot.map((m) => {
                  const isOwner = user && user !== false && user.id === m.user_id;
                  const isFull = m.convoy_id && m.convoy_members_count >= m.convoy_max_size;
                  return (
                    <li key={m.id} data-testid={`spot-livemeet-${m.id}`} className="rounded-xl bg-black/30 border border-white/5 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-bold text-sm truncate">{m.title}</div>
                          <div className="text-[11px] text-white/50 mt-0.5 inline-flex items-center gap-1">
                            <UserIcon className="w-3 h-3" />
                            <UserLink userId={m.user_id} name={m.user_name} className="hover:text-white" />
                          </div>
                          {m.convoy_id && (
                            <Link
                              to={`/convoys/${m.convoy_id}`}
                              className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#00F0FF] hover:text-white transition"
                            >
                              <Users className="w-3 h-3" />
                              {m.convoy_name}
                              <span className="text-white/40 font-normal">
                                · {m.convoy_members_count}/{m.convoy_max_size}
                              </span>
                            </Link>
                          )}
                        </div>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF007F] text-white text-[9px] font-black uppercase tracking-widest flex-shrink-0">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
                        </span>
                      </div>
                      {m.convoy_id && !isOwner && (
                        <button
                          type="button"
                          data-testid={`spot-livemeet-join-${m.id}`}
                          onClick={() => joinConvoy(m)}
                          disabled={isFull}
                          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider transition bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <LogIn className="w-3.5 h-3.5" />
                          {isFull
                            ? (t('livemeet.convoy_full') || 'Convoi complet')
                            : (t('livemeet.join_convoy') || 'Demander à rejoindre')}
                        </button>
                      )}
                      {isOwner && (
                        <button
                          type="button"
                          data-testid={`spot-livemeet-stop-${m.id}`}
                          onClick={() => stopLiveMeet(m)}
                          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider transition border border-[#FF007F]/40 text-[#FF007F] hover:bg-[#FF007F]/10"
                        >
                          <Square className="w-3.5 h-3.5 fill-current" />
                          {t('livemeet.stop_btn') || 'Arrêter le car meet live'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Start a live car meet directly from this spot */}
          {user && user !== false && onStartLiveMeet && (
            <Button
              data-testid="spot-start-livemeet-btn"
              onClick={onStartLiveMeet}
              className="mt-4 w-full bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-bold uppercase tracking-wide rounded-full"
            >
              <Radio className="w-4 h-4 mr-2 animate-pulse" />
              {t('spot.start_livemeet') || 'Lancer un car meet live ici'}
            </Button>
          )}

          {/* Comments */}
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="w-4 h-4 text-white/60" />
              <h3 className="font-display font-bold uppercase tracking-wider text-sm text-white/80">
                {t('spot.comments.title')} ({comments.length})
              </h3>
            </div>

            <ChatComposer
              onSubmit={addComment}
              placeholder={user && user !== false ? t('spot.comments.placeholder') : t('spot.comments.placeholder_disabled')}
              disabled={!user || user === false}
              rows={2}
              sendVariant="cyan"
              testIdPrefix="spot-comment"
            />

            <div className="mt-4">
              {tree.length === 0 ? (
                <div className="text-center text-white/40 text-sm py-6">
                  {t('spot.comments.empty')}
                </div>
              ) : (
                <CommentThread
                  tree={tree}
                  onReply={addReply}
                  onDelete={deleteComment}
                  onLike={likeComment}
                  canModerate={canModerateComment}
                  currentUserId={user && user !== false ? user.id : null}
                  onImageClick={setLightbox}
                />
              )}
            </div>
          </div>
        </div>
      </SheetContent>
      <ImageLightbox src={lightbox} alt="" onClose={() => setLightbox(null)} />
    </Sheet>
  );
}
